/**
 * AI Model Configuration Types
 *
 * Types for dynamic AI model configuration stored in DynamoDB.
 * Allows switching models (Gemini, DeepGram, etc.) without app releases.
 */
// =============================================================================
// Service Types
// =============================================================================
/** AI service types that can have configurable models */
export const AIServiceType = {
    /** Mid-meeting coaching/suggestion generation (Cerebras) */
    COACHING: 'coaching',
    /** Chat/Responses API (OpenAI Luna) */
    CHAT: 'chat',
    /** Real-time transcription (DeepGram) */
    TRANSCRIPTION: 'transcription',
    /** KB content generation (summaries, analysis) */
    KB_GENERATION: 'kb_generation',
    /** AI-powered email generation (follow-ups, etc.) */
    EMAIL_GENERATION: 'email_generation',
    /** Attendee research for meeting prep (OpenAI + web search) */
    ATTENDEE_RESEARCH: 'attendee_research',
    /** Talking points generation for meeting prep (Cerebras) */
    TALKING_POINTS: 'talking_points',
    /** Provider-assisted OCR for standalone KB image imports. */
    KB_IMAGE_OCR: 'kb_image_ocr',
    /** Text translation */
    TRANSLATION: 'translation',
    /** Deal insight generation */
    DEAL_INSIGHTS: 'deal_insights',
    /** Coaching analysis (post-meeting) */
    COACHING_ANALYSIS: 'coaching_analysis',
    /** Learning path generation */
    LEARNING_PATH: 'learning_path',
    /** Transcript-based meeting analysis (post-meeting) */
    MEETING_ANALYSIS: 'meeting_analysis',
    /** Text and multimodal embedding (gemini-embedding-2). Changing invalidates all stored vectors. */
    EMBEDDING: 'embedding',
    /** Memory layer: ambiguous-band free-form supersession classifier (Cerebras). */
    MEMORY_TIEBREAKER: 'memory_tiebreaker',
    /** Memory layer: per-entity insight derivation engine (Cerebras periodic batch). */
    MEMORY_DERIVE: 'memory_derive',
    /** Memory layer: structured extraction from complete email threads. */
    MEMORY_EMAIL_EXTRACTION: 'memory_email_extraction',
    /** Revont Pipelines: classify a sent email against the deal's open gates (Cerebras). */
    EMAIL_GATE_MATCH: 'email_gate_match',
    /** Meeting Prep generation: primary objective + expected outcome (OpenAI Luna). */
    MEETING_PREP_GENERATION: 'meeting_prep_generation',
    /** Meeting notes enhancement: cleans up / structures raw meeting notes (OpenAI Luna). */
    MEETING_NOTES_ENHANCEMENT: 'meeting_notes_enhancement',
    /** Deal forecast prediction: per-deal win-probability + factors from assembled signals (Cerebras). */
    DEAL_FORECAST: 'deal_forecast',
    /** AI-assisted, declarative CSV/XLSX migration mapping. */
    DATA_MIGRATION_MAPPING: 'data_migration_mapping',
    /** Domain-grounded enrichment for Clients created from synced Contacts. */
    CLIENT_ENRICHMENT: 'client_enrichment',
    /** Schema-aware TipTap generation and transforms (OpenAI Luna + web search). */
    EDITOR_ASSIST: 'editor_assist',
};
/** AI provider types */
export const AIProvider = {
    GEMINI: 'gemini',
    DEEPGRAM: 'deepgram',
    OPENAI: 'openai',
    ANTHROPIC: 'anthropic',
    CEREBRAS: 'cerebras',
};
/** Product cap for each list in the attendee/contact research brief. */
export const ATTENDEE_RESEARCH_MAX_LIST_ITEMS = 2;
// =============================================================================
// Default Values
// =============================================================================
/** Default model configurations (used when seeding DynamoDB) */
export const DEFAULT_AI_MODEL_CONFIGS = {
    [AIServiceType.EDITOR_ASSIST]: {
        serviceType: AIServiceType.EDITOR_ASSIST,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        // Structural edits need enough reasoning to preserve section boundaries.
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.COACHING]: {
        serviceType: AIServiceType.COACHING,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        // gpt-oss-120b is a reasoning model - chain-of-thought eats max tokens if
        // not capped. thinkingBudget: 0 maps to reasoning_effort: 'low' in the
        // Cerebras provider so the budget goes to user-visible content.
        // 700 was still too tight for a grounded multi-part answer plus the required
        // structured metadata. This is a maximum, not a target, so short replies do
        // not consume the full allowance.
        config: { maxOutputTokens: 1200, thinkingBudget: 0 },
    },
    [AIServiceType.CHAT]: {
        serviceType: AIServiceType.CHAT,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.TRANSCRIPTION]: {
        serviceType: AIServiceType.TRANSCRIPTION,
        provider: AIProvider.DEEPGRAM,
        modelId: 'nova-3',
        isActive: true,
        config: {},
    },
    [AIServiceType.KB_GENERATION]: {
        serviceType: AIServiceType.KB_GENERATION,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-5.6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.EMAIL_GENERATION]: {
        serviceType: AIServiceType.EMAIL_GENERATION,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 4096, reasoningEffort: 'medium' },
    },
    [AIServiceType.ATTENDEE_RESEARCH]: {
        serviceType: AIServiceType.ATTENDEE_RESEARCH,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { maxOutputTokens: 8192, reasoningEffort: 'medium' },
    },
    [AIServiceType.TALKING_POINTS]: {
        serviceType: AIServiceType.TALKING_POINTS,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.KB_IMAGE_OCR]: {
        serviceType: AIServiceType.KB_IMAGE_OCR,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium', maxOutputTokens: 8192 },
    },
    [AIServiceType.TRANSLATION]: {
        serviceType: AIServiceType.TRANSLATION,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.DEAL_INSIGHTS]: {
        serviceType: AIServiceType.DEAL_INSIGHTS,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { reasoningEffort: 'low' },
    },
    [AIServiceType.COACHING_ANALYSIS]: {
        serviceType: AIServiceType.COACHING_ANALYSIS,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 4096, reasoningEffort: 'medium' },
    },
    [AIServiceType.LEARNING_PATH]: {
        serviceType: AIServiceType.LEARNING_PATH,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    [AIServiceType.MEETING_ANALYSIS]: {
        serviceType: AIServiceType.MEETING_ANALYSIS,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    /**
     * Changing this model invalidates all stored embeddings. Re-indexing required before cutover.
     */
    [AIServiceType.EMBEDDING]: {
        serviceType: AIServiceType.EMBEDDING,
        provider: AIProvider.GEMINI,
        modelId: 'gemini-embedding-2',
        isActive: true,
        config: { outputDimensionality: 768 },
    },
    /**
     * Memory tiebreaker - single-purpose classifier for ambiguous-band free-form
     * fact supersession. Output is a single token: 'updates' | 'extends' | 'unrelated'.
     * Temperature 0 for determinism; tiny output cap.
     */
    [AIServiceType.MEMORY_TIEBREAKER]: {
        serviceType: AIServiceType.MEMORY_TIEBREAKER,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 32, thinkingBudget: 0 },
    },
    /**
     * Memory derive engine - per-entity batched LLM call producing inferred insights
     * from a cluster of supporting facts. Uses a small output cap.
     */
    [AIServiceType.MEMORY_DERIVE]: {
        serviceType: AIServiceType.MEMORY_DERIVE,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 800, thinkingBudget: 0 },
    },
    /**
     * Email memory extraction emits several structured arrays in one response.
     * Keep this separate from the compact derive engine so its output budget is
     * large enough for strict JSON without making every derive call more costly.
     */
    [AIServiceType.MEMORY_EMAIL_EXTRACTION]: {
        serviceType: AIServiceType.MEMORY_EMAIL_EXTRACTION,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { maxOutputTokens: 4096, reasoningEffort: 'medium' },
    },
    /**
     * Email-gate matcher - classifies a sent email against the deal's open
     * stage gates and returns a list of matches with confidence scores. Uses a
     * small output cap for one bounded matches array.
     */
    [AIServiceType.EMAIL_GATE_MATCH]: {
        serviceType: AIServiceType.EMAIL_GATE_MATCH,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 600, thinkingBudget: 0 },
    },
    /**
     * Meeting Prep generation - produces a concise primary objective + expected
     * outcome from deal/client/memory context.
     */
    [AIServiceType.MEETING_PREP_GENERATION]: {
        serviceType: AIServiceType.MEETING_PREP_GENERATION,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { reasoningEffort: 'medium' },
    },
    /**
     * Meeting notes enhancement - cleans up and structures raw meeting notes.
     */
    [AIServiceType.MEETING_NOTES_ENHANCEMENT]: {
        serviceType: AIServiceType.MEETING_NOTES_ENHANCEMENT,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { maxOutputTokens: 8192, reasoningEffort: 'medium' },
    },
    /**
     * Deal forecast prediction - per-deal win-probability + top factors from
     * assembled deal/meeting signals (Cerebras).
     */
    [AIServiceType.DEAL_FORECAST]: {
        serviceType: AIServiceType.DEAL_FORECAST,
        provider: AIProvider.CEREBRAS,
        modelId: 'gpt-oss-120b',
        isActive: true,
        config: { maxOutputTokens: 1200, reasoningEffort: 'medium' },
    },
    [AIServiceType.DATA_MIGRATION_MAPPING]: {
        serviceType: AIServiceType.DATA_MIGRATION_MAPPING,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { maxOutputTokens: 16000, reasoningEffort: 'medium' },
    },
    [AIServiceType.CLIENT_ENRICHMENT]: {
        serviceType: AIServiceType.CLIENT_ENRICHMENT,
        provider: AIProvider.OPENAI,
        modelId: 'gpt-6-luna',
        isActive: true,
        config: { maxOutputTokens: 2048, reasoningEffort: 'medium' },
    },
};
